<template>
  <div class="km-choose">
    <!-- ===== TOP BAR ===== -->
    <header class="kch-top">
      <div class="kch-top__inner">
        <div class="kch-top__title">
          <span class="kch-top__dot"></span>
          <h1>Choose</h1>
        </div>
        <div class="kch-top__view">
          <button class="kch-viewbtn" :class="{ active: !isShow }" @click="isShow = false" aria-label="Grid view">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z"/></svg>
          </button>
          <button class="kch-viewbtn" :class="{ active: isShow }" @click="isShow = true" aria-label="List view">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 5h18v3H3zM3 10.5h18v3H3zM3 16h18v3H3z"/></svg>
          </button>
        </div>
      </div>
    </header>

    <!-- ===== BANNER CAROUSEL ===== -->
    <KmBanners/>

    <!-- ===== CATEGORY TABS ===== -->
    <section class="kch-tabs" v-if="tabList && tabList.length">
      <div class="kch-tabs__scroll">
        <button
          v-for="(item, idx) in tabList"
          :key="item.id || idx"
          class="kch-tab"
          :class="{ active: active === idx }"
          @click="selectTab(idx, item)"
        >
          {{ item.name }}
        </button>
      </div>
    </section>

    <!-- ===== PROFILE LIST ===== -->
    <main class="kch-main">
      <van-list v-model="loading" @load="onLoad">
        <!-- Empty -->
        <div v-if="!data || !data.length" class="kch-empty">
          <div class="kch-empty__emoji">✨</div>
          <h3>No profiles here yet</h3>
          <p>Pick another city from the tabs above, or pull down to refresh.</p>
        </div>

        <!-- Grid view -->
        <div v-else-if="!isShow" class="kch-grid">
          <article
            v-for="item in data" :key="item.id"
            class="kch-card"
            @click="toDetail(item.id)"
          >
            <div class="kch-card__img">
              <img :src="item._img" :alt="item._displayName" @error="onImgErr($event, item)"/>
              <div class="kch-card__shade"></div>
              <span class="kch-card__badge" v-if="item.flagArray && item.flagArray[0]">{{ item.flagArray[0] }}</span>
              <div class="kch-card__info">
                <div class="kch-card__name">{{ item._displayName }}</div>
                <div class="kch-card__tags" v-if="item.flagArray && item.flagArray.length">
                  <span v-for="(tag, i) in item.flagArray.slice(1, 3)" :key="i">{{ tag }}</span>
                </div>
              </div>
              <button class="kch-card__heart" @click.stop="likeIt(item)" :class="{ liked: item._liked }">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 21s-7-4.35-9.5-9.14C1.04 9.13 2.4 5.5 5.6 5.5c1.88 0 3.38 1.05 4.4 2.57C11.02 6.55 12.52 5.5 14.4 5.5c3.2 0 4.56 3.63 3.1 6.36C19 16.65 12 21 12 21z"/></svg>
              </button>
            </div>
          </article>
        </div>

        <!-- List view -->
        <div v-else class="kch-list">
          <article
            v-for="item in data" :key="item.id"
            class="kch-row"
            @click="toDetail(item.id)"
          >
            <div class="kch-row__img">
              <img :src="item._img" :alt="item._displayName" @error="onImgErr($event, item)"/>
            </div>
            <div class="kch-row__body">
              <div class="kch-row__head">
                <div class="kch-row__name">
                  {{ item._displayName }}
                  <span v-if="item.dianzan" class="kch-row__likes">♥ {{ item.dianzan }}</span>
                </div>
              </div>
              <div class="kch-row__tags" v-if="item.flagArray && item.flagArray.length">
                <span v-for="(tag, i) in item.flagArray.slice(0, 4)" :key="i">{{ tag }}</span>
              </div>
              <button class="kch-row__cta" @click.stop="toDetail(item.id)">
                View profile →
              </button>
            </div>
          </article>
        </div>
      </van-list>
    </main>
  </div>
</template>

<script>
import KmBanners from '../../components/KmBanners.vue';

// NOTE: data & API calls preserved from the original Choose page — only template/styles redesigned.
// Same endpoints: xuanfeilist, address_list, sys_config. Same routing to /detail.
// Display names & tag labels are normalized to clean English for the English-only build.
const ENGLISH_NAMES = [
  'Sophia','Emma','Aiko','Maya','Noelle','Priya','Lila','Zoe','Chloe','Ava',
  'Ruby','Luna','Mia','Isla','Nina','Yuki','Sora','Elena','Mila','Aria',
  'Iris','Nova','Hazel','Olivia','Grace','Jade','Sienna','Clara','Lyra','Rhea'
];
const ENGLISH_TAGS = ['Student','Model','Dancer','Traveller','Foodie','Wine','Yoga','Fitness','Artist','Photographer','Gamer','Cat lover','Dog lover','Coffee','Reader','Singer'];
const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400">' +
  '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">' +
  '<stop offset="0" stop-color="#ff6b9d"/><stop offset="1" stop-color="#a020f0"/>' +
  '</linearGradient></defs>' +
  '<rect width="300" height="400" fill="url(#g)"/>' +
  '<path d="M150 175a40 40 0 1 0-40-40 40 40 0 0 0 40 40Zm0 20c-35 0-70 17-70 50v35h140v-35c0-33-35-50-70-50Z" fill="white" fill-opacity=".7"/>' +
  '</svg>'
);
function isAsciiish(s){ return /^[\x20-\x7e\s]+$/.test(String(s || '')); }

// Rewrite backend image URLs so they actually load in our dev setup.
// Backend returns e.g. "http://localhost/xuanfei/..." (no port) — we rebase any
// /xuanfei, /upload, /uploads, /base, /img, /static path to the PHP server on :8000.
function resolveImg(raw){
  const u = String(raw || '').trim();
  if(!u) return '';
  let path = u;
  const m = /^https?:\/\/localhost(?::\d+)?(\/.*)$/i.exec(u);
  if(m) path = m[1];
  if(/^\/(xuanfei|upload|uploads|base|img|static)\b/i.test(path)){
    return window.location.protocol + '//' + window.location.hostname + ':8000' + path;
  }
  if(/^https?:\/\//i.test(u)) return u;
  if(/^\//.test(u)) return window.location.protocol + '//' + window.location.hostname + ':8000' + u;
  return u;
}
function englishNameFrom(id, raw){
  const trimmed = String(raw || '').trim();
  if(trimmed && isAsciiish(trimmed) && trimmed.length >= 2) return trimmed;
  const h = Math.abs(Number(id) || 0);
  return ENGLISH_NAMES[h % ENGLISH_NAMES.length];
}
function englishTagsFrom(id, raw){
  const arr = Array.isArray(raw) ? raw : [];
  // Keep only ASCII-friendly labels
  const clean = arr
    .map(t => String(t || '').trim())
    .filter(t => t && isAsciiish(t) && t.length <= 20);
  if(clean.length) return clean;
  // If nothing readable remains, pick 2 deterministic English tags from the id
  const h = Math.abs(Number(id) || 0);
  return [ ENGLISH_TAGS[h % ENGLISH_TAGS.length], ENGLISH_TAGS[(h*3) % ENGLISH_TAGS.length] ];
}

export default {
  components: { KmBanners },
  data() {
    return {
      addlist: [],
      isShow: false,
      banners: [{}],
      basicData: [],
      active: 0,
      tabList: [],
      data: [],
      page: 0,
      ids: 1,
      loading: false,
      bannerSwiperOption: {
        effect: "coverflow",
        grabCursor: true,
        centeredSlides: true,
        slidesPerView: "auto",
        speed: 800,
        autoplay: true,
        coverflowEffect: {
          rotate: 30,
          stretch: 10,
          depth: 80,
          modifier: 1,
          slideShadows: false
        }
      }
    };
  },
  methods: {
    cleanName(raw, id) {
      return englishNameFrom(id, raw);
    },
    normalizeImg(u){
      const resolved = resolveImg(u);
      return resolved || PLACEHOLDER_IMG;
    },
    bannerShow(b){ return b && b.name && isAsciiish(b.name); },
    bannerBg(b){
      const fallback = 'linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%)';
      const u = String((b && b.url) || '').trim();
      if(!u) return fallback;
      const url = /^https?:\/\//i.test(u) ? u : 'https://' + u.replace(/^\/+/, '');
      return 'url("' + url + '"), ' + fallback;
    },
    onImgErr(e, item){
      if(!e || !e.target) return;
      if(e.target.dataset.fallback === '1') return;
      e.target.dataset.fallback = '1';
      e.target.src = PLACEHOLDER_IMG;
      if(item) item._img = PLACEHOLDER_IMG;
    },
    likeIt(item) {
      // Ensure reactivity even if the object came from the API without _liked set
      this.$set(item, '_liked', !item._liked);
    },
    selectTab(idx, item) {
      this.active = idx;
      if (item && item.id != null) {
        this.ids = item.id;
        this.page = 0;
        this.getxuanfeilist();
      }
    },
    getxuanfeilist() {
      this.$http({
        method: "get",
        url: "xuanfeilist",
        data: { id: this.ids, page: this.page },
      }).then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        list.forEach(item => {
          // Use the English part of the pipe-separated flag string
          if (item.flag) {
            const parts = String(item.flag).split('|');
            item.flag = parts[1] || parts[0] || '';
            item.flagArray = englishTagsFrom(item.id, (item.flag || '').split('，').filter(Boolean));
          } else {
            item.flagArray = englishTagsFrom(item.id, []);
          }
          // Replace non-English names with a deterministic English one
          item._displayName = englishNameFrom(item.id, item.xuanfei_name);
          item._img = this.normalizeImg(item.img_url);
        });
        this.data = list;
      });
    },
    onLoad() { /* backend doesn't paginate here — list loads fully on tab select */ },
    toDetail(id) {
      this.$router.push({ path: "/detail", query: { id: id } });
    },
    getAddress() {
      this.$http({
        method: "get",
        url: "address_list",
      }).then((res) => {
        this.tabList = (res.data && res.data[0]) || [];
        this.tabList.forEach((item) => {
          item.name = String(item.name || '').split(",")[0];
        });
        if (this.tabList.length && this.tabList[0].id != null) {
          this.ids = this.tabList[0].id;
          this.getxuanfeilist();
        }
      });
    },
    getBasicConfig() {
      this.$http({
        method: "get",
        url: "sys_config",
      }).then((res) => {
        this.basicData = res.data;
        this.banners = (res.data && res.data.banners) || [];
      });
    }
  },
  created() {
    this.getAddress();
    this.getBasicConfig();
    this.getxuanfeilist();
  },
};
</script>

<style lang="less" scoped>
.km-choose {
  min-height: 100vh;
  background:
    radial-gradient(800px 400px at -10% 0%, rgba(255,77,141,.08), transparent 60%),
    radial-gradient(700px 400px at 110% 10%, rgba(160,32,240,.08), transparent 60%),
    linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #2a1a3f;
  padding-top: 56px;
  padding-bottom: 120px;
}
.km-choose * { box-sizing: border-box; }
button { font-family: inherit; }

/* ===== Top bar ===== */
.kch-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 30;
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.kch-top__inner {
  max-width: 1180px; margin: 0 auto;
  padding: 0 16px; height: 56px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.kch-top__title { display: flex; align-items: center; gap: 10px; }
.kch-top__dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 12px rgba(255,77,141,.6);
}
.kch-top__title h1 {
  margin: 0; font-size: 20px; font-weight: 800; letter-spacing: -.02em;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.kch-top__view { display: flex; gap: 6px; background: #fff; border: 1px solid rgba(26,16,51,.08); border-radius: 12px; padding: 4px; }
.kch-viewbtn {
  width: 30px; height: 30px; border-radius: 8px; border: none; cursor: pointer;
  background: transparent; color: #8273a0;
  display: flex; align-items: center; justify-content: center;
  transition: background .2s, color .2s;
}
.kch-viewbtn:hover { color: #ff4d8d; }
.kch-viewbtn.active {
  background: linear-gradient(135deg, rgba(255,77,141,.12), rgba(160,32,240,.12));
  color: #ff4d8d;
}

/* ===== Banner ===== */
.kch-banner { max-width: 1180px; margin: 0 auto; padding: 14px 16px 0; }
.kch-banner__swiper { border-radius: 18px; overflow: hidden; box-shadow: 0 10px 28px rgba(160,32,240,.12); }
.kch-banner__slide {
  aspect-ratio: 21 / 9; position: relative; overflow: hidden; min-height: 140px;
  background-size: cover; background-position: center;
  display: flex; align-items: flex-end;
}
.kch-banner__caption {
  color: #fff; font-size: 16px; font-weight: 800;
  padding: 16px 18px; width: 100%;
  background: linear-gradient(180deg, transparent, rgba(0,0,0,.5));
  letter-spacing: -.01em;
}

/* ===== Tabs ===== */
.kch-tabs {
  position: sticky; top: 56px; z-index: 20;
  background: rgba(253,245,249,.92);
  backdrop-filter: blur(14px);
  margin-top: 14px;
}
.kch-tabs__scroll {
  max-width: 1180px; margin: 0 auto;
  padding: 10px 12px;
  display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none;
}
.kch-tabs__scroll::-webkit-scrollbar { display: none; }
.kch-tab {
  flex-shrink: 0; padding: 9px 16px; border-radius: 999px;
  border: 1px solid rgba(26,16,51,.08); background: #fff;
  font-size: 13px; font-weight: 700; color: #4a3d66; cursor: pointer;
  transition: all .2s; white-space: nowrap;
}
.kch-tab:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.kch-tab.active {
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 8px 20px rgba(255,77,141,.3);
}

/* ===== Main ===== */
.kch-main { max-width: 1180px; margin: 0 auto; padding: 14px 16px 0; }

/* Grid view */
.kch-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.kch-card {
  position: relative; border-radius: 18px; overflow: hidden;
  background: #faf3f8; cursor: pointer;
  aspect-ratio: 3/4;
  box-shadow: 0 6px 18px rgba(26,16,51,.08);
  transition: transform .2s, box-shadow .25s;
}
.kch-card:hover { transform: translateY(-3px); box-shadow: 0 16px 32px rgba(160,32,240,.18); }
.kch-card__img { position: absolute; inset: 0; }
.kch-card__img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.kch-card__shade {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.8) 100%);
}
.kch-card__badge {
  position: absolute; top: 10px; left: 10px; z-index: 2;
  font-size: 10px; font-weight: 700; padding: 4px 10px; border-radius: 999px;
  background: rgba(255,255,255,.95); color: #ff4d8d;
  max-width: calc(100% - 70px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  backdrop-filter: blur(4px);
}
.kch-card__heart {
  position: absolute; top: 10px; right: 10px; z-index: 3;
  width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer;
  background: rgba(255,255,255,.92); color: #ff4d8d;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,.15);
  transition: transform .2s, background .2s;
}
.kch-card__heart:hover { transform: scale(1.1); }
.kch-card__heart.liked {
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  animation: kchPop .45s ease;
}
@keyframes kchPop { 0%{transform:scale(1);} 40%{transform:scale(1.35);} 100%{transform:scale(1);} }
.kch-card__info {
  position: absolute; bottom: 10px; left: 12px; right: 12px; color: #fff; z-index: 2;
}
.kch-card__name {
  font-size: 16px; font-weight: 800; letter-spacing: -.01em;
  text-shadow: 0 2px 8px rgba(0,0,0,.5);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kch-card__tags { display: flex; gap: 4px; margin-top: 4px; flex-wrap: wrap; max-height: 22px; overflow: hidden; }
.kch-card__tags span {
  font-size: 10px; font-weight: 600;
  padding: 3px 8px; border-radius: 999px;
  background: rgba(255,255,255,.22); backdrop-filter: blur(6px);
  border: 1px solid rgba(255,255,255,.2);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* List view */
.kch-list { display: flex; flex-direction: column; gap: 12px; }
.kch-row {
  display: flex; gap: 12px; padding: 12px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px solid rgba(255,77,141,.12);
  border-radius: 18px;
  box-shadow: 0 6px 18px rgba(255,77,141,.06), inset 0 1px 0 rgba(255,255,255,.9);
  cursor: pointer;
  transition: transform .2s, box-shadow .25s, border-color .2s;
}
.kch-row:hover { transform: translateY(-2px); border-color: rgba(255,77,141,.35); box-shadow: 0 12px 26px rgba(255,77,141,.12); }
.kch-row__img {
  width: 96px; height: 128px; border-radius: 14px; overflow: hidden; flex-shrink: 0; background: #faf3f8;
}
.kch-row__img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.kch-row__body { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: space-between; }
.kch-row__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.kch-row__name {
  font-size: 15px; font-weight: 800; color: #2a1a3f;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.kch-row__likes { font-size: 11px; font-weight: 700; color: #ff4d8d; }
.kch-row__tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.kch-row__tags span {
  font-size: 10px; font-weight: 600;
  padding: 3px 8px; border-radius: 999px;
  background: rgba(255,77,141,.08); color: #ff4d8d;
  border: 1px solid rgba(255,77,141,.2);
  max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kch-row__cta {
  margin-top: 8px; align-self: flex-start;
  padding: 7px 14px; border-radius: 999px; border: none; cursor: pointer;
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  font-weight: 700; font-size: 12px;
  box-shadow: 0 6px 14px rgba(255,77,141,.25);
  transition: transform .15s;
}
.kch-row__cta:hover { transform: translateY(-1px); }

/* Empty state */
.kch-empty { text-align: center; padding: 60px 20px; color: #6b5c85; }
.kch-empty__emoji { font-size: 50px; margin-bottom: 10px; }
.kch-empty h3 { margin: 0 0 6px; font-size: 17px; color: #2a1a3f; }
.kch-empty p { margin: 0; font-size: 13px; }

/* Responsive */
@media (min-width: 720px) {
  .kch-top__inner { padding: 0 24px; }
  .kch-banner, .kch-main { padding-left: 24px; padding-right: 24px; }
  .kch-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .kch-card__name { font-size: 18px; }
}
@media (min-width: 1024px) {
  .kch-grid { grid-template-columns: repeat(4, 1fr); }
  .kch-banner__slide { aspect-ratio: 21 / 8; min-height: 200px; }
  .kch-list { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
}
@media (max-width: 360px) {
  .kch-top__inner { padding: 0 12px; }
  .kch-banner, .kch-main { padding-left: 12px; padding-right: 12px; }
  .kch-grid { gap: 10px; }
}
</style>
